<template>
  <SuperAdminShell>
    <div class="super-admin-page sa-list-page">
      <ListPageHeader
        title="Aprovações"
        subtitle="Lista de clientes para aprovação de vendas"
      />

      <div class="super-admin-page__shell sa-list-page__shell">
        <div class="sa-list-page__container">
          <div v-if="!hasReady" class="sa-list-page__state">
            <p>Carregando aprovações...</p>
          </div>

          <template v-else>
            <ReportsSummary :metrics="summaryMetrics" aria-label="Resumo das aprovações" />

            <div class="sa-list-filters">
              <div class="sa-list-filters__field sa-list-filters__field--grow">
                <label for="approve-filter-name">Nome</label>
                <input
                  id="approve-filter-name"
                  type="text"
                  v-model="searchTerm"
                  class="sa-list-filters__control"
                  placeholder="Digite o nome da empresa..."
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__field">
                <label for="approve-filter-cnpj">CNPJ</label>
                <input
                  id="approve-filter-cnpj"
                  type="text"
                  v-model="searchCnpj"
                  class="sa-list-filters__control"
                  placeholder="Digite o CNPJ..."
                  @input="onSearch"
                />
              </div>
              <div class="sa-list-filters__field">
                <label for="approve-filter-system">Sistema</label>
                <select
                  id="approve-filter-system"
                  v-model="selectedType"
                  class="sa-list-filters__control"
                  @change="filterByType"
                >
                  <option value="">Todos</option>
                  <option value="1">Weeduc</option>
                  <option value="2">Weflix</option>
                </select>
              </div>
              <div class="sa-list-filters__field">
                <label for="approve-filter-status">Status</label>
                <select
                  id="approve-filter-status"
                  v-model="selectedStatus"
                  class="sa-list-filters__control"
                  @change="onSearch"
                >
                  <option value="">Todos</option>
                  <option value="0">Pendente</option>
                  <option value="1">Aprovado</option>
                </select>
              </div>
              <div class="sa-list-filters__actions">
                <button
                  type="button"
                  class="sa-list-filters__clear"
                  :disabled="!hasActiveFilters"
                  @click="clearFilters"
                >
                  Limpar filtros
                </button>
              </div>
            </div>

            <div class="sa-list-page__results">
              <p class="sa-list-page__results-count">{{ resultsLabel }}</p>
            </div>

            <div v-if="!filteredCompanys.length" class="sa-list-page__empty">
              <h3>Nenhuma aprovação encontrada</h3>
              <p>Altere os filtros para encontrar outros resultados.</p>
              <button type="button" class="btnSave" @click="clearFilters">Limpar filtros</button>
            </div>

            <template v-else>
              <div class="sa-rep-table-wrap">
                <table class="sa-rep-table">
                  <thead>
                    <tr>
                      <th scope="col" class="is-id">ID</th>
                      <th scope="col" class="is-company">Nome</th>
                      <th scope="col">CNPJ</th>
                      <th scope="col">Sistema</th>
                      <th scope="col">Telefone</th>
                      <th scope="col">E-mail</th>
                      <th scope="col">Status</th>
                      <th scope="col">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="company in paginatedCompanies" :key="company.id">
                      <td class="is-id is-muted">{{ company.id }}</td>
                      <td class="is-company">
                        <span class="sa-rep-table__name">{{ company.name }}</span>
                      </td>
                      <td class="is-muted">{{ formatCnpj(company.idFiscal) }}</td>
                      <td><SystemBadge :type-system="company.typeSystem" /></td>
                      <td class="is-muted">{{ formatPhone(company.phone) }}</td>
                      <td class="is-muted">{{ company.email || '—' }}</td>
                      <td>
                        <span
                          class="sa-list-status"
                          :class="company.cartStatus == 1 ? 'sa-list-status--approved' : 'sa-list-status--pending'"
                        >
                          {{ company.cartStatus == 1 ? 'Aprovado' : 'Pendente' }}
                        </span>
                      </td>
                      <td class="sa-list-actions">
                        <button
                          v-if="company.cartStatus != 1"
                          type="button"
                          class="sa-list-approve-btn"
                          :disabled="loadingApproval === company.id"
                          title="Aprovar venda"
                          @click="approveSale(company.id)"
                        >
                          <fa v-if="loadingApproval !== company.id" icon="fa-solid fa-check" />
                          <span v-else class="sa-list-spinner" aria-hidden="true" />
                          <span>Aprovar Venda</span>
                        </button>
                        <span v-else class="sa-list-approve-done">Venda Aprovada</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <ReportsPagination
                :page="currentPage"
                :total-pages="totalPages"
                :page-size="pageSize"
                @update:page="currentPage = $event"
                @update:pageSize="onPageSizeChange"
              />
            </template>
          </template>
        </div>
      </div>
    </div>
    <Loader :hasReady="hasReady" />
  </SuperAdminShell>
</template>

<script>
import SuperAdminShell from '@/Views/Weflix/SuperAdminShell.vue';
import { mapState } from 'vuex';
import superAdmin from '@/utils/superAdmin';
import Loader from '@/components/Weflix/Loader.vue';
import ListPageHeader from '@/components/Weflix/SuperAdmin/ListPageHeader.vue';
import ReportsSummary from '@/components/Weflix/SuperAdmin/Reports/ReportsSummary.vue';
import ReportsPagination from '@/components/Weflix/SuperAdmin/Reports/ReportsPagination.vue';
import SystemBadge from '@/components/Weflix/SuperAdmin/Reports/SystemBadge.vue';
import { formatNumberBR } from '@/utils/superAdmin/formatters';

export default {
  components: {
    SuperAdminShell,
    Loader,
    ListPageHeader,
    ReportsSummary,
    ReportsPagination,
    SystemBadge,
  },
  data() {
    return {
      listCompanys: [],
      selectedType: '',
      selectedStatus: '',
      searchTerm: '',
      searchCnpj: '',
      hasReady: false,
      currentPage: 1,
      pageSize: 10,
      loadingApproval: null,
    };
  },
  computed: {
    ...mapState(['token']),
    hasActiveFilters() {
      return Boolean(
        this.searchTerm
        || this.searchCnpj
        || this.selectedType
        || this.selectedStatus !== '',
      );
    },
    filteredCompanys() {
      let filtered = this.listCompanys;

      if (this.selectedType) {
        filtered = filtered.filter((company) => company.typeSystem == this.selectedType);
      }

      if (this.selectedStatus !== '') {
        filtered = filtered.filter((company) => company.cartStatus == this.selectedStatus);
      }

      if (this.searchTerm && this.searchTerm.trim() !== '') {
        const search = this.searchTerm.toLowerCase().trim();
        filtered = filtered.filter(
          (company) => company.name && company.name.toLowerCase().includes(search),
        );
      }

      if (this.searchCnpj && this.searchCnpj.trim() !== '') {
        const search = this.searchCnpj.replace(/[^\d]/g, '');
        filtered = filtered.filter((company) => {
          if (!company.idFiscal) return false;
          const cnpj = company.idFiscal.replace(/[^\d]/g, '');
          return cnpj.includes(search);
        });
      }

      return filtered;
    },
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.pageSize;
      return this.filteredCompanys.slice(start, start + this.pageSize);
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.filteredCompanys.length / this.pageSize));
    },
    resultsLabel() {
      const count = this.filteredCompanys.length;
      return `${formatNumberBR(count)} ${count === 1 ? 'resultado' : 'resultados'}`;
    },
    summaryMetrics() {
      const rows = this.filteredCompanys;
      const pending = rows.filter((c) => Number(c.cartStatus) !== 1).length;
      const approved = rows.filter((c) => Number(c.cartStatus) === 1).length;
      const weeduc = rows.filter((c) => Number(c.typeSystem) === 1).length;
      return [
        { label: 'Total', value: formatNumberBR(rows.length) },
        { label: 'Pendentes', value: formatNumberBR(pending) },
        { label: 'Aprovadas', value: formatNumberBR(approved) },
        { label: 'Weeduc', value: formatNumberBR(weeduc) },
      ];
    },
  },
  methods: {
    formatCnpj(value) {
      if (!value) return '—';
      return String(value).replace(
        /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
        '$1.$2.$3/$4-$5',
      );
    },
    formatPhone(value) {
      if (!value) return '—';
      return String(value).replace(/^(\d{2})(\d{4,5})(\d{4})$/, '($1) $2-$3');
    },
    async listCompany() {
      try {
        this.hasReady = false;
        const response = await superAdmin.listCompany(this.token);
        this.listCompanys = response;
        this.hasReady = true;
      } catch (error) {
        console.error(error);
        this.hasReady = true;
      }
    },
    async approveSale(companyId) {
      try {
        this.loadingApproval = companyId;
        await superAdmin.approveSale(this.token, companyId);

        const company = this.listCompanys.find((c) => c.id === companyId);
        if (company) {
          company.cartStatus = 1;
        }

        this.$toast?.success('Venda aprovada com sucesso!');
      } catch (error) {
        console.error('Erro ao aprovar venda:', error);
        this.$toast?.error('Erro ao aprovar venda. Tente novamente.');
      } finally {
        this.loadingApproval = null;
      }
    },
    filterByType() {
      this.currentPage = 1;
    },
    onSearch() {
      this.currentPage = 1;
    },
    onPageSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },
    clearFilters() {
      this.searchTerm = '';
      this.searchCnpj = '';
      this.selectedType = '';
      this.selectedStatus = '';
      this.currentPage = 1;
    },
  },
  created() {
    this.listCompany();
  },
};
</script>

<style>
@import url('@/css/weflix/super-admin-list.css');
@import url('@/components/Weflix/SuperAdmin/Reports/report-table.css');
</style>
